import { useLocale } from './chunk-SBDRB6T7.js';
import { Map, LngLatBounds } from 'mapbox-gl';
import { useEffect, useRef, useState } from 'react';
import { useMount } from 'react-use';
import 'mapbox-gl/dist/mapbox-gl.css';

// src/constants/mapbox.ts
var MAPBOX_KEY = "pk.eyJ1IjoibWVpbGxldXJzYWdlbnRzIiwiYSI6ImNscnFqMWlvMzAxcTAya3J1Mnhnb2d2aXgifQ.Z10AXF0CuUKANeMh6NPcmA";
var useBaseMap = (props) => {
  const ref = useRef(null);
  const [map, setMap] = useState();
  const locale = useLocale();
  useMount(() => {
    if (ref.current) {
      const nextMap = new Map({
        ...props,
        container: ref.current,
        style: "mapbox://styles/mapbox/streets-v12"
      });
      nextMap.setLanguage(locale);
      nextMap.on("load", () => setMap(nextMap));
    }
  });
  return [ref, map];
};

// src/helpers/hooks/map/useMap.ts
var RADIUS_SOURCE = "radius-source";
var RADIUS_LAYER = "radius-layer";
var RADIUS_LAYER_DOT = "radius-layer-dot";
var MULTIPOLYGON_SOURCE = "multipolygon-source";
var MULTIPOLYGON_LAYER = "multipolygon-layer";
var MULTIPOLYGON_LAYER_BORDER = "multipolygon-layer-border";
var useMap = ({
  radius,
  multipolygon,
  colors,
  disableZoom,
  disablePan,
  mapView = "radius" /* RADIUS */,
  ...mapProps
}) => {
  const [ref, map] = useBaseMap(mapProps);
  const calculateBounds = (coordinates) => {
    const bounds = new LngLatBounds();
    coordinates.forEach((group) => {
      group.forEach((ring) => {
        ring.forEach(([lng, lat]) => {
          bounds.extend([lng, lat]);
        });
      });
    });
    return bounds;
  };
  useEffect(() => {
    if (!map)
      return;
    if (!map.getSource(RADIUS_SOURCE)) {
      map.addSource(RADIUS_SOURCE, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });
    }
    if (!map.getSource(MULTIPOLYGON_SOURCE)) {
      map.addSource(MULTIPOLYGON_SOURCE, {
        type: "geojson",
        data: {
          type: "FeatureCollection",
          features: []
        }
      });
    }
    if (!map.getLayer(RADIUS_LAYER)) {
      map.addLayer({
        id: RADIUS_LAYER,
        type: "circle",
        source: RADIUS_SOURCE,
        layout: {
          visibility: "none"
        },
        paint: {
          "circle-radius": [
            "interpolate",
            ["exponential", 2],
            ["zoom"],
            0,
            0,
            20,
            ["/", ["/", ["*", ["get", "radius"], 1e3], 0.075], ["cos", ["*", ["get", "lat"], ["/", Math.PI, 180]]]]
          ],
          "circle-color": colors.content,
          "circle-opacity": 0.2,
          "circle-stroke-color": colors.border,
          "circle-stroke-width": 2
        }
      });
    }
    if (!map.getLayer(RADIUS_LAYER_DOT)) {
      map.addLayer({
        id: RADIUS_LAYER_DOT,
        type: "circle",
        source: RADIUS_SOURCE,
        layout: {
          visibility: "none"
        },
        paint: {
          "circle-radius": 7,
          "circle-color": colors.default,
          "circle-stroke-color": colors.border,
          "circle-stroke-width": 2
        }
      });
    }
    if (!map.getLayer(MULTIPOLYGON_LAYER)) {
      map.addLayer({
        id: MULTIPOLYGON_LAYER,
        type: "fill",
        source: MULTIPOLYGON_SOURCE,
        layout: {
          visibility: "none"
        },
        paint: {
          "fill-color": colors.content || colors.default,
          "fill-opacity": 0.3,
          "fill-outline-color": colors.border
        }
      });
      map.addLayer({
        id: MULTIPOLYGON_LAYER_BORDER,
        type: "line",
        source: MULTIPOLYGON_SOURCE,
        layout: {
          visibility: "none"
        },
        paint: {
          "line-color": colors.border || colors.default,
          "line-width": 3,
          "line-opacity": 1
        }
      });
      if (map.getLayer(RADIUS_LAYER_DOT)) {
        map.moveLayer(RADIUS_LAYER_DOT);
      }
    }
    if (disableZoom) {
      map.scrollZoom.disable();
      map.doubleClickZoom.disable();
      map.boxZoom.disable();
      map.touchZoomRotate.disable();
    }
    if (disablePan) {
      map.dragPan.disable();
      map.keyboard.disable();
      map.touchZoomRotate.disableRotation();
    }
  }, [map]);
  useEffect(() => {
    if (!map)
      return;
    if (multipolygon && multipolygon.length > 0) {
      const source = map.getSource(MULTIPOLYGON_SOURCE);
      source.setData({
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {},
            geometry: {
              type: "MultiPolygon",
              coordinates: multipolygon
            }
          }
        ]
      });
      const bounds = calculateBounds(multipolygon);
      map.fitBounds(bounds, {
        padding: 50,
        duration: 800
      });
    } else if (mapProps.center) {
      const source = map.getSource(RADIUS_SOURCE);
      source.setData({
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            properties: {
              lat: mapProps.center.lat,
              radius
            },
            geometry: {
              type: "Point",
              coordinates: [mapProps.center.lng, mapProps.center.lat]
            }
          }
        ]
      });
      map.setLayoutProperty(RADIUS_LAYER, "visibility", "visible");
      map.setLayoutProperty(RADIUS_LAYER_DOT, "visibility", "visible");
    }
  }, [map, multipolygon, mapProps.center, radius]);
  useEffect(() => {
    if (!map)
      return;
    const visibility = (layer, visible) => map.setLayoutProperty(layer, "visibility", visible ? "visible" : "none");
    visibility(MULTIPOLYGON_LAYER, mapView === "multipolygon" /* MULTIPOLYGON */);
    visibility(MULTIPOLYGON_LAYER_BORDER, mapView === "multipolygon" /* MULTIPOLYGON */);
    visibility(RADIUS_LAYER, mapView === "radius" /* RADIUS */);
    visibility(RADIUS_LAYER_DOT, mapView !== "none" /* NONE */);
  }, [map, mapView]);
  useEffect(() => {
    if (!map || !mapProps.center || multipolygon)
      return;
    map.easeTo({
      center: mapProps.center,
      duration: 800,
      zoom: mapProps.zoom
    });
  }, [map, multipolygon, mapProps.center, mapProps.zoom]);
  return [ref];
};

export { MAPBOX_KEY, useMap };
//# sourceMappingURL=out.js.map
//# sourceMappingURL=chunk-DNUFCL26.js.map